import { useEffect, useState } from "react";
import { MailCheck, Paperclip, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "@/lib/format";
import { fetchReportEmails } from "@/lib/m365";
import { cn } from "@/lib/utils";

/**
 * EmailHistory – «E-post til kunde» i rapportdetalj: kven sende, til kven, når – og knapp for å sende (igjen).
 * refreshKey: bump for å hente lista på nytt etter sending.
 */
export const EmailHistory = ({ reportId, refreshKey = 0, onSend, sending = false, testid = "report-email-history" }) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!reportId) return;
    let alive = true;
    fetchReportEmails(reportId)
      .then((list) => alive && setItems(list))
      .catch(() => alive && setItems([]));
    return () => {
      alive = false;
    };
  }, [reportId, refreshKey]);

  const sent = (items || []).length > 0;

  return (
    <div className={cn("rounded-xl border p-3", sent ? "border-[rgba(31,138,76,0.35)] bg-[rgba(31,138,76,0.05)]" : "bg-muted/30")} data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <MailCheck className="h-3.5 w-3.5" /> E-post til kunde
        </div>
        {onSend && (
          <Button size="sm" variant={sent ? "outline" : "default"} className="h-9" onClick={onSend} disabled={sending} data-testid={`${testid}-send-button`}>
            {sending ? <Loader2 className="animate-spin" /> : <Send />} {sent ? "Send igjen" : "Send til kunde"}
          </Button>
        )}
      </div>

      {items === null ? (
        <div className="mt-2 space-y-2">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      ) : items.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground" data-testid={`${testid}-empty`}>
          Ikkje sendt på e-post enno. PDF-en blir lagt ved automatisk når du sender frå Outlook-kontoen din.
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-border/70" data-testid={`${testid}-list`}>
          {items.map((e) => (
            <li key={e.id} className="py-2 text-sm first:pt-1 last:pb-0" data-testid={`email-history-row-${e.id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <span className="min-w-0 truncate font-semibold" data-testid={`email-history-to-${e.id}`}>
                  Til {(e.to || []).join(", ")}
                  {e.cc?.length > 0 && <span className="font-normal text-muted-foreground"> · kopi {e.cc.join(", ")}</span>}
                </span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground" data-testid={`email-history-at-${e.id}`}>{formatDateTime(e.sent_at)}</span>
              </div>
              <div className="mt-0.5 truncate text-xs text-muted-foreground">{e.subject}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                <span>Sendt av {e.sent_by_name || "—"}{e.sent_from ? ` (${e.sent_from})` : ""}</span>
                {e.attachment && (
                  <span className="inline-flex items-center gap-1"><Paperclip className="h-3 w-3" /> {e.attachment}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
